import { initReactI18next } from 'react-i18next';

import i18n from 'i18next';

import { getAppConfig, getLocales } from '@/services/common';
import { getUrlValue } from '@/utils';

import { setAntdLocale } from './antd';
import ar from './ar';
import { setDayjsLocale } from './dayjs';
import en from './en';
import {
  type StaticLang,
  createI18nResources,
  deepMerge,
  detectSystemLang,
  normalizeAppLang,
  shouldEnableRtl,
} from './lang';
import { parseRemoteLocales } from './parse-remote';
import tr from './tr';

export { antdLocales, getAntdLocale } from './antd';
export {
  APP_LANGS,
  createI18nResources,
  deepMerge,
  detectSystemLang,
  getAntdLang,
  getDayjsLang,
  getStaticBaseLang,
  normalizeAppLang,
  shouldEnableRtl,
  tryMapAppLang,
} from './lang';
export { parseRemoteLocales } from './parse-remote';

const locales: Record<StaticLang, { translation: Record<string, any> }> = {
  en: {
    translation: en,
  },
  ar: {
    translation: ar,
  },
  tr: {
    translation: tr,
  },
};

const getLocaleCacheUrlKey = (lang: LangType) => `locale_cache_url_${lang}`;
const getLocaleCacheTextKey = (lang: LangType) => `locale_cache_text_${lang}`;

function setLang() {
  // 只认 lang；有非空值则归一化（fr → en，不用系统语言覆盖）；空则读系统语言
  const rawUrlLang = String(window.query?.lang ?? getUrlValue('lang') ?? '').trim();
  const lang = rawUrlLang ? normalizeAppLang(rawUrlLang) : detectSystemLang();
  localStorage.setItem('lang', lang);
  return (window.lang = lang);
}

export async function setupI18n() {
  const lang = setLang();

  document.body.classList.toggle('mirror', shouldEnableRtl(lang));

  setAntdLocale(lang);
  setDayjsLocale(lang);

  // 始终用静态基线创建当前语言 bundle，避免 locales[lang] 为 undefined 导致白屏
  const resources = createI18nResources(lang, locales);

  try {
    // 远程配置必须使用原始语言码（尤其是 en-IN，不可归一成 en）
    const appConfigRes = await getAppConfig(lang);
    const strResourceUrl = appConfigRes?.data?.strResourceUrl;
    if (strResourceUrl) {
      const cacheUrlKey = getLocaleCacheUrlKey(lang);
      const cacheTextKey = getLocaleCacheTextKey(lang);
      const cachedUrl = localStorage.getItem(cacheUrlKey);
      const cachedText = localStorage.getItem(cacheTextKey) || '';

      let remoteLocalesText = '';
      if (cachedUrl === strResourceUrl && cachedText) {
        remoteLocalesText = cachedText;
      } else {
        remoteLocalesText = await getLocales(strResourceUrl);
        localStorage.setItem(cacheUrlKey, strResourceUrl);
        localStorage.setItem(cacheTextKey, remoteLocalesText || '');
      }

      const remoteLocales = parseRemoteLocales(remoteLocalesText || '');
      resources[lang].translation = deepMerge(resources[lang].translation, remoteLocales);
    }
  } catch (error) {
    console.error('setupI18n remote locales failed:', error);
  }

  i18n.use(initReactI18next).init({
    lng: lang,
    fallbackLng: 'en',
    resources,
    interpolation: {
      escapeValue: false,
    },
  });
}
